import { useState, useMemo, useEffect, useRef } from 'react';
import { useQueryParams } from './';

/**
 * Compares two items according to a sort config. Exported so a caller that sorts a secondary list
 * outside the hook — the child rows in a DataTable expansion, say — orders it identically.
 *
 * @param {object} a
 * @param {object} b
 * @param {{key: string, direction: string, groupByColumn?: string}} sortConfig
 * @returns {number} Negative, zero or positive, as Array.prototype.sort expects
 */
export const compareItems = (a, b, sortConfig) => {
  // Do not sort grouped columns
  if (
    sortConfig.groupByColumn &&
    a[sortConfig.groupByColumn] &&
    b[sortConfig.groupByColumn] &&
    a[sortConfig.groupByColumn] == b[sortConfig.groupByColumn]
  ) {
    return 0;
  }

  if (a[sortConfig.key] === null) {
    return sortConfig.direction === 'asc' ? 1 : -1;
  }
  if (b[sortConfig.key] === null) {
    return sortConfig.direction === 'asc' ? -1 : 1;
  }
  // strings
  if (isNaN(a[sortConfig.key]) && isNaN(b[sortConfig.key])) {
    if (a[sortConfig.key]?.toLowerCase() < b[sortConfig.key]?.toLowerCase()) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key]?.toLowerCase() > b[sortConfig.key]?.toLowerCase()) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    // numbers
  } else {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
  }
  return 0;
};

export const useSortableData = (items, defaultSortConfig) => {
  const { queryParams, setHistory } = useQueryParams();

  const [sortConfig, setSortConfig] = useState(queryParams.sort || defaultSortConfig);

  // Whether the user has explicitly chosen a sort (clicked a header, or arrived with ?sort in the
  // URL). Guards the default-sort sync below so it never overrides an explicit choice.
  const userSortedRef = useRef(Boolean(queryParams.sort));

  // defaultSortConfig may only become available (or change) after mount — e.g. it depends on data
  // that loads asynchronously. useState captures only the initial value, so apply the current
  // default whenever it appears/changes, unless the user has chosen their own sort.
  useEffect(() => {
    if (defaultSortConfig && !userSortedRef.current) {
      setSortConfig(defaultSortConfig);
    }
    // Depend on the primitive key/direction (not the object identity) so an inline-literal
    // defaultSortConfig prop doesn't re-fire this effect every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSortConfig?.key, defaultSortConfig?.direction]);

  const sortedItems = useMemo(() => {
    let sortableItems = [...items];
    if (sortConfig) {
      sortableItems.sort((a, b) => compareItems(a, b, sortConfig));
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key, groupByColumn) => {
    userSortedRef.current = true;

    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key, direction, groupByColumn });
    setHistory({ sort: { key, direction, groupByColumn } });
  };

  return { sortedItems, requestSort, sortConfig };
};
